import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { getEndpoints } from "./ApiContext";

let instance = null;

class WebSocketService {
  constructor(configOrGetEndpoints) {
    if (instance) {
      return instance;
    }
    this.forceConnectAfterLogin = false;
    this.ws = null;
    this.initialMessageSent = false;
    this.forceNewTopic = false;
    this.reconnectTimeout = null;
    this.responseTimeout = null;
    this.reconnectAttempts = 0;
    this.MAX_RECONNECT_ATTEMPTS = 3;
    this.configOrGetEndpoints = configOrGetEndpoints;
    this.onMessageCallbacks = [];
    this.onErrorCallbacks = [];
    this.onCloseCallbacks = [];
    this.onTopicRefreshCallbacks = [];
    this.onConnectionStatusCallbacks = [];
    this.isConnecting = false;
    this.messageQueue = [];
    this.lastConnectionAttemptTime = 0;
    this.CONNECTION_COOLDOWN = 3000;
    this.lastPingTime = Date.now();
    this.pingInterval = null;
    this.connectionPromise = null;
    this.wsUrl = null;
    this.lastContextUpdate = null;
    this.currentCollection = null;
    this.currentTopicId = null;
    this.timeout = null;
    this.isInitialConnection = true;
    this.debugInfo = {
      connected: false,
      lastError: null,
      lastConnectionAttempt: null,
      messagesSent: 0,
      messagesReceived: 0,
      reconnectCount: 0,
      connectionBlockedCount: 0,
    };
  
    instance = this;
  }

  addMessageListener(callback) {
    if (
      typeof callback === "function" &&
      !this.onMessageCallbacks.includes(callback)
    ) {
      this.onMessageCallbacks.push(callback);
      return true;
    }
    return false;
  }

  addErrorListener(callback) {
    if (
      typeof callback === "function" &&
      !this.onErrorCallbacks.includes(callback)
    ) {
      this.onErrorCallbacks.push(callback);
      return true;
    }
    return false;
  }

  addCloseListener(callback) {
    if (
      typeof callback === "function" &&
      !this.onCloseCallbacks.includes(callback)
    ) {
      this.onCloseCallbacks.push(callback);
      return true;
    }
    return false;
  }

  addTopicRefreshListener(callback) {
    if (
      typeof callback === "function" &&
      !this.onTopicRefreshCallbacks.includes(callback)
    ) {
      this.onTopicRefreshCallbacks.push(callback);
      return true;
    }
    return false;
  }

  addConnectionStatusListener(callback) {
    if (
      typeof callback === "function" &&
      !this.onConnectionStatusCallbacks.includes(callback)
    ) {
      this.onConnectionStatusCallbacks.push(callback);
      return true;
    }
    return false;
  }

  removeMessageListener(callback) {
    this.onMessageCallbacks = this.onMessageCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  removeErrorListener(callback) {
    this.onErrorCallbacks = this.onErrorCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  removeCloseListener(callback) {
    this.onCloseCallbacks = this.onCloseCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  removeTopicRefreshListener(callback) {
    this.onTopicRefreshCallbacks = this.onTopicRefreshCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  removeConnectionStatusListener(callback) {
    this.onConnectionStatusCallbacks = this.onConnectionStatusCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  async getWebSocketURL() {
    try {
      if (this.wsUrl) {
        return this.wsUrl;
      }

      if (this.configOrGetEndpoints) {
        if (typeof this.configOrGetEndpoints === "function") {
          try {
            const endpoints = await this.configOrGetEndpoints();
            if (endpoints && endpoints.API_WEBSOCKET) {
              this.wsUrl = endpoints.API_WEBSOCKET;
              return this.wsUrl;
            }
          } catch (error) {
            console.error(
              "[WebSocketService] Error al obtener endpoints:",
              error
            );
          }
        } else if (this.configOrGetEndpoints.API_WEBSOCKET) {
          this.wsUrl = this.configOrGetEndpoints.API_WEBSOCKET;
          return this.wsUrl;
        }
      }

      try {
        const apiData = await getEndpoints();
        if (apiData && apiData.API_WEBSOCKET) {
          this.wsUrl = apiData.API_WEBSOCKET;
          return this.wsUrl;
        }
      } catch (error) {
        console.error(
          "[WebSocketService] Error al obtener endpoints desde apiContext:",
          error
        );
      }

      if (!this.wsUrl) {
        try {
          const currentProtocol =
            window.location.protocol === "https:" ? "wss:" : "ws:";
          const currentDomain = window.location.hostname;

          let fallbackUrl = null;

          if (currentDomain.includes(".")) {
            const domainParts = currentDomain.split(".");
            if (domainParts.length >= 2) {
              const baseDomain = domainParts
                .slice(domainParts.length - 2)
                .join(".");
              fallbackUrl = `${currentProtocol}//ws.${baseDomain}/ws`;
            }
          }

          if (!fallbackUrl) {
            fallbackUrl = `${currentProtocol}//${currentDomain}/ws`;
          }

          this.wsUrl = fallbackUrl;
          return this.wsUrl;
        } catch (error) {
          console.error(
            "[WebSocketService] Error al generar URL de fallback:",
            error
          );
          throw new Error("No se pudo obtener una URL válida para WebSocket");
        }
      }

      throw new Error("No se pudo obtener URL del WebSocket de ninguna fuente");
    } catch (error) {
      this.debugInfo.lastError = `Error al obtener URL WebSocket: ${error.message}`;
      console.error(
        "[WebSocketService] Error al obtener la URL del WebSocket:",
        error
      );
      throw error;
    }
  }

  shouldAllowConnection() {
    const now = Date.now();

    if (now - this.lastConnectionAttemptTime < this.CONNECTION_COOLDOWN) {
      this.debugInfo.connectionBlockedCount++;
      return false;
    }

    return true;
  }

  resetAllState() {
    this.close();
    this.initialMessageSent = false;
    this.isInitialConnection = true;
    this.currentTopicId = null;
    this.currentCollection = null;
    this.messageQueue = [];
    this.reconnectAttempts = 0;
    this.lastContextUpdate = null;
    this.debugInfo.connected = false;
    this.debugInfo.reconnectCount = 0;
    this.forceNewTopic = true;
    
    return true;
  }
  async connect() {
    const isForced = this.forceConnectAfterLogin;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.debugInfo.connected = true;
      this.notifyConnectionStatus(true);
      return Promise.resolve();
    }
    if (isForced) {
    } else if (this.messageQueue.length === 0 && !this.initialMessageSent && !this.currentTopicId) {
      return Promise.resolve();
    }

    if (this.isConnecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    if (!this.shouldAllowConnection()) {
      return this.connectionPromise || Promise.resolve();
    }

    this.lastConnectionAttemptTime = Date.now();
    this.isConnecting = true;
    this.debugInfo.lastConnectionAttempt = new Date().toISOString();
    this.notifyConnectionStatus(false);

    this.connectionPromise = new Promise(async (resolve, reject) => {
      try {
        if (this.ws) {
          this.ws.onclose = null;
          this.ws.onerror = null;
          this.ws.onopen = null;
          this.ws.onmessage = null;

          if (this.ws.readyState !== WebSocket.CLOSED) {
            this.ws.close(1000, "Preparando nueva conexión");
          }

          this.ws = null;
        }

        this.reconnectAttempts += 1;
        this.debugInfo.reconnectCount += 1;
        const wsPath = await this.getWebSocketURL();
        const newWs = new WebSocket(wsPath);

        newWs.onopen = () => {
          this.ws = newWs;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.debugInfo.connected = true;
          this.setupPingInterval();
          this.processQueue();
          this.notifyConnectionStatus(true);
          resolve();
        };

        newWs.onmessage = (event) => {
          this.debugInfo.messagesReceived += 1;
          try {
            const data = JSON.parse(event.data);

            if (data.type === "pong") return;

            this.onMessageCallbacks.forEach((callback) => {
              try {
                callback(data);
              } catch (error) {
                console.error(
                  "[WebSocketService] Error en callback de mensaje:",
                  error
                );
              }
            });
          } catch (error) {
            console.error(
              "[WebSocketService] Error al procesar mensaje:",
              error
            );
          }
        };

        newWs.onerror = (error) => {
          console.error("[WebSocketService] Error en WebSocket:", error);
          this.debugInfo.lastError = `Error de WebSocket: ${error.message}`;
          this.isConnecting = false;
          this.debugInfo.connected = false;
          this.notifyConnectionStatus(false);

          if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
            this.reconnectAttempts = 0;
            this.triggerErrorCallbacks(
              "Error de conexión. Por favor, intenta nuevamente."
            );
            reject(new Error("Máximo de intentos de reconexión alcanzado"));
          } else {
            this.scheduleReconnect();
            reject(error);
          }
        };

        newWs.onclose = (event) => {
          this.isConnecting = false;
          this.debugInfo.connected = false;
          this.notifyConnectionStatus(false);
          this.connectionPromise = null;

          if (event.code !== 1000) {
            this.triggerCloseCallbacks(event);

            if (this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
              this.scheduleReconnect();
            } else {
              this.reconnectAttempts = 0;
              this.triggerErrorCallbacks(
                "La conexión se ha cerrado. Por favor, intenta nuevamente."
              );
            }
          }

          this.clearPingInterval();
        };
      } catch (error) {
        console.error(
          "[WebSocketService] Error al configurar WebSocket:",
          error
        );
        this.debugInfo.lastError = `Error de configuración: ${error.message}`;
        this.isConnecting = false;
        this.debugInfo.connected = false;
        this.connectionPromise = null;
        this.notifyConnectionStatus(false);

        if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
          this.reconnectAttempts = 0;
          this.triggerErrorCallbacks(
            "Error al establecer conexión. Por favor, intenta nuevamente."
          );
          reject(error);
        } else {
          this.scheduleReconnect();
          reject(error);
          
        }
      }
    });
    if (isForced) {
      this.forceConnectAfterLogin = false;
    }
    return this.connectionPromise.catch((error) => {});
  }

  notifyConnectionStatus(isConnected) {
    this.onConnectionStatusCallbacks.forEach((callback) => {
      try {
        callback(isConnected);
      } catch (error) {}
    });
  }

  triggerTopicsRefresh() {
    this.onTopicRefreshCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {}
    });

    this.onMessageCallbacks.forEach((callback) => {
      try {
        callback({ type: "topics_refresh" });
      } catch (error) {}
    });
  }

  triggerErrorCallbacks(errorMessage) {
    this.onErrorCallbacks.forEach((callback) => {
      try {
        callback(errorMessage);
      } catch (error) {}
    });
  }

  triggerCloseCallbacks(event) {
    this.onCloseCallbacks.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {}
    });
  }

  setWebSocketURL(url) {
    if (url && typeof url === "string") {
      this.wsUrl = url;
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.close();
      }
    }
  }

  setupPingInterval() {
    this.clearPingInterval();

    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: "ping" }));
          this.lastPingTime = Date.now();
        } catch (error) {}
      } else {
        this.connect().catch((err) =>
          console.error("[WebSocketService] Error de reconexión en ping:", err)
        );
      }
    }, 60000);
  }

  clearPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    const delay = 2000 * Math.pow(1.5, this.reconnectAttempts - 1);

    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch((err) =>
        console.error("[WebSocketService] Error en reconexión programada:", err)
      );
    }, delay);
  }
  
  resetForLogin() {    
    this.close();
    this.initialMessageSent = false;
    this.isInitialConnection = true;
    this.currentTopicId = null;
    this.currentCollection = null;
    this.messageQueue = [];
    this.reconnectAttempts = 0;
    this.lastContextUpdate = null;
    this.forceConnectAfterLogin = true;
    this.debugInfo.connected = false;
    this.debugInfo.reconnectCount = 0;
    this.isConnecting = false;
    this.connectionPromise = null;  
    return true;
  }


  isSimilarToRecentMessage(message) {
    if (this.lastContextUpdate) {
      const now = Date.now();
      const timeSinceLastUpdate = now - this.lastContextUpdate.time;

      if (
        timeSinceLastUpdate < 2000 &&
        message.collection === this.lastContextUpdate.collection &&
        message.topic_id === this.lastContextUpdate.topic_id &&
        ((message.query === "" && !this.lastContextUpdate.wasQuery) ||
          message.query === this.lastContextUpdate.lastQuery)
      ) {
        return true;
      }
    }

    return false;
  }

  async send(message) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      try {
        const hasSimilarMessage = this.messageQueue.some(
          (queuedMsg) =>
            queuedMsg.collection === message.collection &&
            queuedMsg.topic_id === message.topic_id &&
            queuedMsg.query === message.query
        );

        if (!hasSimilarMessage) {
          this.messageQueue.push(message);
        }

        await this.connect();

        this.processQueue();

        return;
      } catch (connectionError) {
        console.error(
          "[WebSocketService] Error al reconectar:",
          connectionError
        );
        return;
      }
    }

    try {
      const messageString = JSON.stringify(message);
      this.ws.send(messageString);
      this.debugInfo.messagesSent += 1;

      if (message.query && message.query !== "") {
        this.lastContextUpdate = {
          time: Date.now(),
          collection: message.collection,
          topic_id: message.topic_id,
          wasQuery: true,
          lastQuery: message.query,
        };

        this.currentCollection = message.collection;
        this.currentTopicId = message.topic_id;
      }
    } catch (error) {
      console.error("[WebSocketService] Error al enviar mensaje:", error);
      this.messageQueue.push(message);

      try {
        await this.connect();
        this.processQueue();
      } catch (connectionError) {
        console.error(
          "[WebSocketService] Error al reconectar después de fallo de envío:",
          connectionError
        );
      }
    }
  }

  processQueue() {
    if (
      this.messageQueue.length === 0 ||
      !this.ws ||
      this.ws.readyState !== WebSocket.OPEN
    ) {
      return;
    }
    const uniqueMessages = [];
    const messageSeen = new Set();

    this.messageQueue.forEach((message) => {
      const key = `${message.collection}-${message.topic_id}-${message.query}`;
      if (!messageSeen.has(key)) {
        messageSeen.add(key);
        uniqueMessages.push(message);
      }
    });
    this.messageQueue = [];

    uniqueMessages.forEach((message) => {
      try {
        const messageString = JSON.stringify(message);
        this.ws.send(messageString);
        this.debugInfo.messagesSent += 1;
        this.currentCollection = message.collection;
        this.currentTopicId = message.topic_id;

        if (message.query !== "") {
          this.lastContextUpdate = {
            time: Date.now(),
            collection: message.collection,
            topic_id: message.topic_id,
            wasQuery: true,
            lastQuery: message.query,
          };
        }
      } catch (error) {
        console.error(
          "[WebSocketService] Error al procesar mensaje en cola:",
          error
        );
        this.messageQueue.push(message);
      }
    });
  }

  updateContext(collection, topicId = "", resetContext = true) {
    this.currentCollection = collection;
    this.currentTopicId = topicId;
  
    if (!collection) {
      return false;
    }
  
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const accessToken = Cookies.get("accessToken");
      if (accessToken) {
        const messageData = {
          collection: collection,
          topic_id: topicId,
          Authorization: `Bearer ${accessToken}`,
          query: "",
          context_update: true,
          exclusive_collection: true,
          reset_context: resetContext,
          collection_change: true
        };
        this.sendContextUpdate(messageData);
      }
    }
  
    this.lastContextUpdate = {
      time: Date.now(),
      collection: collection,
      topic_id: topicId,
      wasQuery: false,
      lastQuery: "",
    };
    return true;
  }

  updateCollectionWithoutReset(collection, topicId = "") {
    return this.updateContext(collection, topicId, false);
  }

  sendContextUpdate(message) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.messageQueue.push(message);
      this.connect();
      return;
    }

    try {
      const messageString = JSON.stringify(message);
      this.ws.send(messageString);
      this.debugInfo.messagesSent += 1;
    } catch (error) {
      console.error(
        "[WebSocketService] Error al enviar actualización de contexto:",
        error
      );
      this.messageQueue.push(message);
      this.connect().catch((err) =>
        console.error("[WebSocketService] Error al reconectar:", err)
      );
    }
  }

  sendChatMessage(inputText, config = {}, currentTopic = null) {
    if (!inputText || !inputText.trim()) {
      return false;
    }
    
    this.initialMessageSent = true;
  
    const isGuest = !Cookies.get("accessToken");
    let messageData;
  
    const topicId = this.isInitialConnection || this.forceNewTopic ? "" : 
                   (currentTopic ? currentTopic.id : this.currentTopicId || "");
  
    const collection = this.currentCollection || config?.DEFAULT_COLLECTION || "";
  
    if (isGuest) {
      const guestToken =
        "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJHVUVTVF9VU0VSIiwiY29nbml0bzpncm91cHMiOlsiR1VFU1RfR1JPVVAiXSwiaXNzIjoiZnJvbnQiLCJ0b2tlbl91c2UiOiJhY2Nlc3MiLCJ1c2VybmFtZSI6IkdVRVNUX1VTRVIifQ.PcqpR_wHkLVs1_hnnThLqPfyq9huDLAEPgOojKSVXJo";
  
      messageData = {
        query: inputText,
        collection: collection,
        Authorization: `Bearer ${guestToken}`,
        topic_id: topicId,
        isNewChat: this.isInitialConnection,
        exclusive_collection: true,
        maintain_conversation: true
      };
    } else {
      const accessToken = Cookies.get("accessToken");
      messageData = {
        query: inputText,
        collection: collection,
        Authorization: `Bearer ${accessToken}`,
        topic_id: topicId,
        isNewChat: this.isInitialConnection,
        maintain_conversation: true
      };
    }
        
    if (this.isInitialConnection) {
      this.isInitialConnection = false;
    }
    
    this.forceNewTopic = false;
    
    this.send(messageData);
    return true;
  }

  async loadChatHistoryForTopic(topicId) {
    this.currentTopicId = topicId;

    if (this.currentCollection) {
      this.updateContext(this.currentCollection, topicId);
    }

    return true;
  }

  resetWebSocketConnection() {
    this.reconnectAttempts = 0;

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
      this.responseTimeout = null;
    }

    this.close();
    return this.connect();
  }

  close() {
    this.clearPingInterval();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;

      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, "Cierre explícito");
      }

      this.ws = null;
    }

    this.messageQueue = [];
    this.isConnecting = false;
    this.connectionPromise = null;
    this.debugInfo.connected = false;
  }

  forceReconnect() {
    this.close();
    return this.connect();
  }

  getWebSocket() {
    return this.ws;
  }

  getDebugInfo() {
    return {
      ...this.debugInfo,
      wsState: this.ws
        ? ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][this.ws.readyState]
        : "NO_CONNECTION",
      queuedMessages: this.messageQueue.length,
      registeredCallbacks: {
        message: this.onMessageCallbacks.length,
        error: this.onErrorCallbacks.length,
        close: this.onCloseCallbacks.length,
        topicRefresh: this.onTopicRefreshCallbacks.length,
        connectionStatus: this.onConnectionStatusCallbacks.length,
      },
      wsUrl: this.wsUrl,
      lastConnectionTime: this.lastConnectionAttemptTime
        ? new Date(this.lastConnectionAttemptTime).toISOString()
        : null,
      currentContext: {
        collection: this.currentCollection,
        topicId: this.currentTopicId,
      },
      pingInterval: this.pingInterval ? "Activo" : "Inactivo",
      lastPing: this.lastPingTime
        ? new Date(this.lastPingTime).toISOString()
        : null,
    };
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  static getInstance(configOrGetEndpoints, forceNew = false) {
    if (!instance || forceNew) {
      if (forceNew && instance) {
        instance = null;
      }
      return new WebSocketService(configOrGetEndpoints);
    }
  
    if (
      configOrGetEndpoints &&
      instance.configOrGetEndpoints !== configOrGetEndpoints
    ) {
      instance.configOrGetEndpoints = configOrGetEndpoints;
    }
  
    return instance;
  }
}
export default WebSocketService;